/**
 * Dev-only helpers for profiling PWA / resume API storms.
 * Exposed on `window.__sukhaWakeDebug` in development.
 */

import { getWakeRecoveryMetricsSnapshot } from "@sukha/query";

export type SukhaWakeDebugApi = {
  /** In-memory wake recovery flush metrics from `@sukha/query`. */
  getWakeMetrics: () => ReturnType<typeof getWakeRecoveryMetricsSnapshot>;
  /** Log current metrics to the console. */
  logWakeMetrics: () => void;
  /** Chrome Performance marks tagged `session:*` (dev only). */
  listSessionMarks: () => PerformanceMark[];
};

function listSessionPerformanceMarks(): PerformanceMark[] {
  if (typeof performance === "undefined" || !("getEntriesByType" in performance)) {
    return [];
  }
  return performance
    .getEntriesByType("mark")
    .filter((entry): entry is PerformanceMark =>
      entry.name.startsWith("session:"),
    );
}

export function createWakeDebugApi(): SukhaWakeDebugApi {
  return {
    getWakeMetrics: () => getWakeRecoveryMetricsSnapshot(),
    logWakeMetrics: () => {
      const metrics = getWakeRecoveryMetricsSnapshot();
      // eslint-disable-next-line no-console -- this dev tool's purpose IS console output; it is exposed on window in development only.
      console.info("[sukhaWakeDebug] wake recovery metrics", metrics);
    },
    listSessionMarks: () => listSessionPerformanceMarks(),
  };
}

declare global {
  interface Window {
    __sukhaWakeDebug?: SukhaWakeDebugApi;
  }
}

/** Attach dev wake debug API once (no-op in production). */
export function installDevWakeRecoveryDebug(): void {
  if (process.env.NODE_ENV !== "development") return;
  if (typeof window === "undefined") return;
  if (window.__sukhaWakeDebug) return;
  window.__sukhaWakeDebug = createWakeDebugApi();
}
